import React, { useMemo } from "react";
import { Box, Card, Chip, Divider, Typography } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styles from "./cisoDashboard.module.scss";

const buildMonitorData = () =>
  Array.from({ length: 60 }, (_, index) => {
    const latencyBase =
      13 + Math.sin(index / 7) * 3 + Math.cos(index / 11) * 1.8;
    const decisionsBase =
      52 + Math.sin(index / 6) * 12 + Math.cos(index / 9) * 6;
    return {
      time: `${index}s`,
      latency: Number(latencyBase.toFixed(1)),
      decisions: Math.round(decisionsBase),
    };
  });

export const ContinuousAuthenticationMonitor = () => {
  const data = useMemo(() => buildMonitorData(), []);
  const stats = useMemo(() => {
    const latencyValues = data.map((item) => item.latency);
    const decisionValues = data.map((item) => item.decisions);
    const totalDecisions = decisionValues.reduce(
      (sum, value) => sum + value,
      0
    );
    const avgLatency =
      latencyValues.reduce((sum, value) => sum + value, 0) /
      latencyValues.length;
    const avgDecisions =
      decisionValues.reduce((sum, value) => sum + value, 0) /
      decisionValues.length;
    return {
      peakLatency: Math.max(...latencyValues).toFixed(1),
      minLatency: Math.min(...latencyValues).toFixed(1),
      avgLatency: avgLatency.toFixed(1),
      avgDecisions: Math.round(avgDecisions),
      totalDecisions,
    };
  }, [data]);

  return (
    <Card
      className={styles.card}
      sx={{ borderRadius: 2 }}
      variant="outlined"
      elevation={0}
    >
      <Box className={styles.monitorHeader}>
        <Box className={styles.monitorTitleWrap}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography className={styles.sectionHeading} fontWeight={700}>
              Continuous Authentication Monitor
            </Typography>
            <Box>
              <Chip label={"Live"} variant="outlined" color="success" />
            </Box>
          </Box>
          <Box>
            <Typography className={styles.subtitle}>
              Real-time enforcement latency and decision volume
            </Typography>
          </Box>
        </Box>
        <Box className={styles.monitorMeta}>
          <Box className={styles.monitorStat}>
            <Typography variant="caption" className={styles.monitorStatLabel}>
              Avg Latency
            </Typography>
            <Typography
              className={styles.monitorStatValue}
              sx={{ color: "#0575E6" }}
              fontWeight={500}
            >
              {stats.avgLatency}ms
            </Typography>
          </Box>
          <Box className={styles.monitorStat}>
            <Typography variant="caption" className={styles.monitorStatLabel}>
              Decisions/sec
            </Typography>
            <Typography
              className={styles.monitorStatValue}
              sx={{ color: "#14B8A6" }}
              fontWeight={500}
            >
              {stats.avgDecisions}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={styles.monitorLegend}>
        <Box className={styles.monitorLegendItem}>
          <span
            className={styles.monitorLegendDot}
            style={{ background: "#2563EB" }}
          />
          <Typography variant="caption">Authentication Latency (ms)</Typography>
        </Box>
        <Box className={styles.monitorLegendItem}>
          <span
            className={styles.monitorLegendDot}
            style={{ background: "#14B8A6" }}
          />
          <Typography variant="caption">Decision Volume (/sec)</Typography>
        </Box>
      </Box>

      <Box className={styles.monitorChart}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 12, right: 18, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="time"
              interval={4}
              tick={{ fontSize: 11, fill: "#94A3B8" }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: "#2563EB" }}
              axisLine={{ stroke: "#2563EB" }}
              label={{
                value: "Latency (ms)",
                angle: -90,
                position: "insideLeft",
                fill: "#2563EB",
                fontSize: 11,
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: "#14B8A6" }}
              axisLine={{ stroke: "#14B8A6" }}
              label={{
                value: "Decisions/sec",
                angle: 90,
                position: "insideRight",
                fill: "#14B8A6",
                fontSize: 11,
              }}
            />
            <Tooltip
              formatter={(value, name) =>
                name === "latency"
                  ? [`${value} ms`, "Authentication Latency"]
                  : [`${value} /sec`, "Decision Volume"]
              }
              contentStyle={{ borderRadius: 10, borderColor: "#E5E7EB" }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="latency"
              stroke="#2563EB"
              strokeWidth={2.4}
              dot={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="decisions"
              stroke="#14B8A6"
              strokeWidth={2.4}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box className={styles.monitorMetrics}>
        <Box className={styles.monitorMetric}>
          <Typography className={styles.monitorMetricLabel}>
            Peak Latency
          </Typography>
          <Typography className={styles.monitorMetricValue}>
            {stats.peakLatency}ms
          </Typography>
        </Box>
        <Box className={styles.monitorMetric}>
          <Typography className={styles.monitorMetricLabel}>
            Min Latency
          </Typography>
          <Typography
            className={styles.monitorMetricValue}
            sx={{ color: "#14B8A6" }}
          >
            {stats.minLatency}ms
          </Typography>
        </Box>
        <Box className={styles.monitorMetric}>
          <Typography className={styles.monitorMetricLabel}>
            Total Decisions
          </Typography>
          <Typography className={styles.monitorMetricValue}>
            {stats.totalDecisions.toLocaleString("en-US")}
          </Typography>
        </Box>
        <Box className={styles.monitorMetric}>
          <Typography className={styles.monitorMetricLabel}>
            Success Rate
          </Typography>
          <Typography
            className={styles.monitorMetricValue}
            sx={{ color: "#14B8A6" }}
          >
            99.8%
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default ContinuousAuthenticationMonitor;
